import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { ADMIN_ROLE_ID } from '@/lib/roles';

type RequestKind = 'day_off' | 'preferred_hours';
type RequestStatus = 'pending' | 'approved' | 'rejected' | 'withdrawn';

interface AvailabilityRequest {
  id: string;
  staff_id: string;
  request_date: string;
  request_kind: RequestKind;
  preferred_start_time: string | null;
  preferred_end_time: string | null;
  note: string | null;
  status: RequestStatus;
  decision_note: string | null;
  created_at: string;
}

const STATUS_LABELS: Record<RequestStatus, string> = {
  pending: '確認待ち', approved: '承認', rejected: '却下', withdrawn: '取消済み',
};

function todayJst() {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date());
}

export default function AvailabilityPage() {
  const { user, profile } = useAuth();
  const isAdmin = profile?.role_id === ADMIN_ROLE_ID;
  const [requests, setRequests] = useState<AvailabilityRequest[]>([]);
  const [staffNames, setStaffNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [form, setForm] = useState({
    requestDate: '', requestKind: 'day_off' as RequestKind,
    startTime: '09:00', endTime: '17:00', note: '',
  });

  const fetchRequests = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const query = supabase.from('staff_availability_requests')
      .select('id,staff_id,request_date,request_kind,preferred_start_time,preferred_end_time,note,status,decision_note,created_at')
      .gte('request_date', todayJst()).order('request_date', { ascending: true });
    const { data, error } = isAdmin ? await query : await query.eq('staff_id', user.id);
    if (error) {
      setMessage({ type: 'error', text: '休み希望の取得に失敗しました' });
      setLoading(false);
      return;
    }
    const rows = (data || []) as AvailabilityRequest[];
    setRequests(rows);
    if (isAdmin) {
      const ids = [...new Set(rows.map((row) => row.staff_id))];
      if (ids.length > 0) {
        const { data: profiles } = await supabase.from('profiles').select('id,full_name').in('id', ids);
        const names: Record<string, string> = {};
        (profiles || []).forEach((item) => { names[item.id] = item.full_name; });
        setStaffNames(names);
      }
    }
    setLoading(false);
  }, [isAdmin, user]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const pendingCount = useMemo(
    () => requests.filter((request) => request.status === 'pending').length,
    [requests],
  );

  const submitRequest = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.requestDate) return;
    setSaving(true); setMessage(null);
    const { data, error } = await supabase.rpc('submit_staff_availability_request', {
      p_request_date: form.requestDate,
      p_request_kind: form.requestKind,
      p_preferred_start_time: form.requestKind === 'preferred_hours' ? form.startTime : null,
      p_preferred_end_time: form.requestKind === 'preferred_hours' ? form.endTime : null,
      p_note: form.note.trim() || null,
    });
    const result = data as { success?: boolean; error?: string } | null;
    if (error || !result?.success) {
      setMessage({ type: 'error', text: result?.error === 'reviewed_request_locked' ? '承認・却下済みの希望は変更できません' : '希望を登録できませんでした' });
    } else {
      setMessage({ type: 'success', text: '希望を登録しました' });
      setForm((current) => ({ ...current, requestDate: '', note: '' }));
      await fetchRequests();
    }
    setSaving(false);
  };

  const withdraw = async (id: string) => {
    if (!window.confirm('この希望を取り消しますか？')) return;
    const { data } = await supabase.rpc('withdraw_staff_availability_request', { p_request_id: id });
    const result = data as { success?: boolean } | null;
    setMessage(result?.success ? { type: 'success', text: '希望を取り消しました' } : { type: 'error', text: '取り消しできませんでした' });
    await fetchRequests();
  };

  const review = async (id: string, decision: 'approved' | 'rejected') => {
    const note = window.prompt(decision === 'approved' ? '承認メモ（任意）' : '却下理由（任意）', '') ?? '';
    const { data } = await supabase.rpc('review_staff_availability_request', {
      p_request_id: id, p_decision: decision, p_decision_note: note || null,
    });
    const result = data as { success?: boolean } | null;
    setMessage(result?.success ? { type: 'success', text: decision === 'approved' ? '承認しました' : '却下しました' } : { type: 'error', text: '処理できませんでした' });
    await fetchRequests();
  };

  return (
    <div className="max-w-6xl">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-foreground-900 font-heading">休み・勤務希望</h2>
        <p className="text-sm text-foreground-600 mt-1">希望休または希望する勤務時間を登録できます</p>
      </div>

      {message && <div className={`mb-4 rounded-lg px-4 py-3 text-sm ${message.type === 'success' ? 'bg-accent-100 text-accent-800' : 'bg-red-50 text-red-700'}`}>{message.text}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-5">
        <form onSubmit={submitRequest} className="bg-background-50 rounded-xl border border-background-200/70 p-5 h-fit">
          <h3 className="font-semibold text-foreground-900 mb-4">希望を登録</h3>
          <label className="block text-sm font-medium text-foreground-800 mb-1">日付</label>
          <input type="date" required min={todayJst()} value={form.requestDate} onChange={(e) => setForm({ ...form, requestDate: e.target.value })} className="w-full px-3.5 py-2.5 rounded-lg border border-background-200 bg-background-50 mb-4" />

          <label className="block text-sm font-medium text-foreground-800 mb-1">希望内容</label>
          <select value={form.requestKind} onChange={(e) => setForm({ ...form, requestKind: e.target.value as RequestKind })} className="w-full px-3.5 py-2.5 rounded-lg border border-background-200 bg-background-50 mb-4">
            <option value="day_off">終日休み</option>
            <option value="preferred_hours">希望勤務時間</option>
          </select>

          {form.requestKind === 'preferred_hours' && <div className="grid grid-cols-2 gap-3 mb-4">
            <div><label className="block text-sm text-foreground-700 mb-1">開始</label><input type="time" required value={form.startTime} onChange={(e) => setForm({ ...form, startTime: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border border-background-200" /></div>
            <div><label className="block text-sm text-foreground-700 mb-1">終了</label><input type="time" required value={form.endTime} onChange={(e) => setForm({ ...form, endTime: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border border-background-200" /></div>
          </div>}

          <label className="block text-sm font-medium text-foreground-800 mb-1">備考（任意）</label>
          <textarea maxLength={500} rows={3} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="w-full px-3.5 py-2.5 rounded-lg border border-background-200 bg-background-50 mb-4 resize-none" placeholder="予定など、必要な場合のみ入力" />
          <button disabled={saving} className="w-full px-4 py-2.5 rounded-lg bg-primary-500 text-background-50 font-semibold disabled:opacity-60">{saving ? '登録中…' : '希望を登録'}</button>
        </form>

        <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
          <div className="px-5 py-4 border-b border-background-200 flex justify-between"><h3 className="font-semibold text-foreground-900">今後の希望</h3>{isAdmin && <span className="text-sm text-foreground-600">確認待ち {pendingCount}件</span>}</div>
          {loading ? <div className="py-12 text-center text-foreground-500">読み込み中…</div> : requests.length === 0 ? <div className="py-12 text-center text-sm text-foreground-500">登録された希望はありません</div> : <div className="divide-y divide-background-200">
            {requests.map((request) => <div key={request.id} className="p-5 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1">
                {isAdmin && <p className="font-semibold text-foreground-900">{staffNames[request.staff_id] || 'スタッフ'}</p>}
                <p className="text-sm text-foreground-900">{new Date(`${request.request_date}T00:00:00`).toLocaleDateString('ja-JP', { month: 'long', day: 'numeric', weekday: 'short' })} · {request.request_kind === 'day_off' ? '終日休み' : `${request.preferred_start_time?.slice(0,5)}〜${request.preferred_end_time?.slice(0,5)}`}</p>
                {request.note && <p className="text-xs text-foreground-600 mt-1">{request.note}</p>}
                {request.decision_note && <p className="text-xs text-foreground-600 mt-1">管理者メモ：{request.decision_note}</p>}
              </div>
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${request.status === 'approved' ? 'bg-accent-100 text-accent-800' : request.status === 'rejected' ? 'bg-red-50 text-red-700' : request.status === 'pending' ? 'bg-secondary-100 text-secondary-800' : 'bg-background-200 text-foreground-600'}`}>{STATUS_LABELS[request.status]}</span>
              {request.status === 'pending' && (isAdmin ? <div className="flex gap-2"><button onClick={() => review(request.id, 'approved')} className="px-3 py-1.5 rounded-lg bg-accent-100 text-accent-800 text-xs font-medium">承認</button><button onClick={() => review(request.id, 'rejected')} className="px-3 py-1.5 rounded-lg bg-red-50 text-red-700 text-xs font-medium">却下</button></div> : <button onClick={() => withdraw(request.id)} className="px-3 py-1.5 rounded-lg bg-background-100 text-foreground-700 text-xs font-medium">取消</button>)}
            </div>)}
          </div>}
        </div>
      </div>
    </div>
  );
}
